<script setup>
import { ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import { useToastStore } from "@/stores/toast.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";

/**
 * "Password" section of Manage Account (GH-922) — brand re-skin of the
 * Jetstream update-password form, same Fortify endpoint and error bag.
 */
const passwordInput = ref(null);
const currentPasswordInput = ref(null);

const form = useForm({
    current_password: "",
    password: "",
    password_confirmation: "",
});

const toastStore = useToastStore();

function save() {
    form.put(route("user-password.update"), {
        errorBag: "updatePassword",
        preserveScroll: true,
        onSuccess: () => {
            form.reset();
            toastStore.success("Your password was updated.");
        },
        onError: () => {
            if (form.errors.password) {
                form.reset("password", "password_confirmation");
                passwordInput.value.focus();
            }

            if (form.errors.current_password) {
                form.reset("current_password");
                currentPasswordInput.value.focus();
            }
        },
    });
}

const labelClasses = "mb-1.5 block text-[15px] font-bold text-[#3c3938]";
const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#a79e9b] bg-white px-4 font-brand text-[15px] text-brand-navy focus:border-brand-navy focus:outline-none focus:ring-0";
const errorClasses = "mt-2 text-[13px] font-semibold text-brand-red";
</script>

<template>
    <form @submit.prevent="save">
        <h2 class="font-brand text-[25px] font-bold text-[#102a3d]">
            Password
        </h2>
        <p class="mt-1 font-brand text-[15px] leading-relaxed text-[#3c3938]">
            Ensure your account is using a long, random password to stay secure.
        </p>

        <div class="mt-8 max-w-[433px]">
            <div>
                <label :class="labelClasses" for="current-password"
                    >Current Password</label
                >
                <input
                    id="current-password"
                    ref="currentPasswordInput"
                    v-model="form.current_password"
                    type="password"
                    autocomplete="current-password"
                    :class="inputClasses"
                />
                <p v-if="form.errors.current_password" :class="errorClasses">
                    {{ form.errors.current_password }}
                </p>
            </div>

            <div class="mt-6">
                <label :class="labelClasses" for="new-password"
                    >New Password</label
                >
                <input
                    id="new-password"
                    ref="passwordInput"
                    v-model="form.password"
                    type="password"
                    autocomplete="new-password"
                    :class="inputClasses"
                />
                <p v-if="form.errors.password" :class="errorClasses">
                    {{ form.errors.password }}
                </p>
            </div>

            <div class="mt-6">
                <label :class="labelClasses" for="confirm-password"
                    >Confirm Password</label
                >
                <input
                    id="confirm-password"
                    v-model="form.password_confirmation"
                    type="password"
                    autocomplete="new-password"
                    :class="inputClasses"
                />
                <p
                    v-if="form.errors.password_confirmation"
                    :class="errorClasses"
                >
                    {{ form.errors.password_confirmation }}
                </p>
            </div>

            <div class="mt-8 flex items-center justify-end gap-4">
                <BrandButton
                    type="submit"
                    class="h-11 px-10 text-[15px]"
                    :loading="form.processing"
                >
                    {{ form.processing ? "Saving" : "Save Password" }}
                </BrandButton>
            </div>
        </div>
    </form>
</template>
